import React, { useCallback, useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Modal, Form, Input, Select, InputNumber, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import * as Yup from "yup";
import moment from "moment";
import axios, { AxiosError, AxiosResponse } from "axios";
import { ISkill, IUser } from "..";
import getValidationErrors from "../../../utils/getValidationErrors";
import FormItem from "../../../components/FormItem";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import { removeNullKeys } from "../../../utils/formatters";
import UserRoles from "../../../enums/UserRoles";

const { Option } = Select;

interface IOption {
  label: string;
  value: string;
}

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (user?: IUser) => void;
  user?: IUser;
}

interface IUserFormData {
  name: string;
  email: string;
  phone_number?: string;
  monthly_productive_hours?: number;
  hiring_model: "CORPORATE" | "SHAREHOLDER" | "CLT" | "CLT_TEMP";
  role: UserRoles;
  seniority: "JUNIOR" | "FULL" | "SENIOR";
  manager_id: string;
  admission_date: any;
  dismissal_date: any;
  selected_skills: {
    label: string;
    value: string;
  }[];
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const UserModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  user,
}) => {
  const { t } = useTranslation(["users"]);
  const [loading, setLoading] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [managers, setManagers] = useState<IUser[]>([]);
  const [loadingSkills, setLoadingSkills] = useState<boolean>(false);
  const [skills, setSkills] = useState<ISkill[]>();
  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    undefined,
  );

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!user, [user]);

  useEffect(() => {
    setLoadingSkills(true);
    api
      .get<IUser[]>("/skills")
      .then(response => {
        setSkills(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro ao buscar os skills",
          description: "Occoreu um erro ao buscar os skills, tente novamente.",
        });
      })
      .finally(() => setLoadingSkills(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("/users", {
        params: {
          role: "MANAGER",
        },
      })
      .then(response => {
        setManagers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map(manager => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  const skillsOptions = useMemo(() => {
    if (!skills) {
      return undefined;
    }

    return (
      skills
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
        .map(skill => ({
          label: skill.name,
          value: skill.id,
        }))
    );
  }, [skills]);

  useEffect(() => {
    setSelectedRole(user?.role);
    form.setFieldsValue({
      ...user,
      selected_skills: user?.skills.map(skill => ({
        label: skill.name,
        value: skill.id,
      })),
      admission_date:
        user?.admission_date && moment(user.admission_date.split("T")[0]),
      dismissal_date:
        user?.dismissal_date && moment(user.dismissal_date.split("T")[0]),
    });
  }, [form, user]);

  const handleClose = useCallback(
    (usr?: IUser) => {
      form.resetFields();
      handleCloseModal(usr);
    },
    [form, handleCloseModal],
  );

  const formatDate = useCallback((date: any) => {
    if (!date) {
      return undefined;
    }
    return date.format("YYYY-MM-DD");
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 404:
          description = "O usuário não foi encontrado";
          break;
        case 409:
          description = "O E-mail informado já é usado por outro usuário";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados do usuário, tente novamente"
            : "Ocorreu um erro ao criar o usuário, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing ? "Erro na edição" : "Erro na criação",
        description,
      });
    },
    [addToast, isEditing],
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required("Nome obrigatório"),
          email: Yup.string()
            .email("Digite um e-mail válido")
            .required("E-mail obrigatório"),
          role: Yup.string().required("Função obrigatória"),
          hiring_model: Yup.string().required(
            "Modelo de contratação obrigatório",
          ),
          seniority: Yup.string().required("Perfil obrigatório"),
          manager_id: Yup.string()
            .nullable()
            .when("role", {
              is: role =>
                role !== UserRoles.Manager && role !== UserRoles.Administrative,
              then: Yup.string().required("Manager obrigatório"),
            }),
          monthly_productive_hours: Yup.number().nullable(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const {
          name,
          email,
          phone_number,
          hiring_model,
          role,
          seniority,
          monthly_productive_hours,
          manager_id,
          admission_date,
          dismissal_date,
          selected_skills,
        } = data;

        const pass = v4();

        const payload = {
          name,
          email,
          phone_number,
          hiring_model,
          role,
          seniority,
          monthly_productive_hours,
          manager_id,
          admission_date: formatDate(admission_date),
          dismissal_date: formatDate(dismissal_date),
          status: dismissal_date ? "INACTIVE" : "ACTIVE",
          ...(!isEditing && { password: pass }),
          ...(!isEditing && { password_confirmation: pass }),
          skills_ids: selected_skills?.map(skill => skill.value),
        };

        let response: AxiosResponse;
        if (isEditing) {
          response = await api.put(`/users/${user?.id}`, payload);
          addToast({
            type: "success",
            title: "Usuário alterado",
            description: "O usuário foi alterado com sucesso",
          });
        } else {
          response = await api.post("/users", payload);
          addToast({
            type: "success",
            title: "Usuário criado",
            description: "O usuário foi criado com sucesso",
          });
        }

        handleClose(response.data);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      formatDate,
      generateErrorToast,
      handleClose,
      isEditing,
      user?.id,
    ],
  );

  const handleCreateSkill = useCallback(name => {
    api
      .post<ISkill>("/skills", { name })
      .then(response => {
        setSkills(oldSkills =>
          oldSkills ? [...oldSkills, response.data] : [response.data],
        );
      });
  }, []);

  const handleRoleChange = useCallback(value => {
    setSelectedRole(value);
  }, []);

  const userRolesOptions = useMemo(() => {
    return (Object.keys(UserRoles) as (keyof typeof UserRoles)[]).map(
      (key, _) => {
        return {
          label: t(`roles.${UserRoles[key]}.label`),
          value: UserRoles[key],
        };
      },
    );
  }, [t]);

  return (
    <Modal
      title={isEditing ? t("actions.edit") : t("actions.create")}
      visible={visible}
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        <FormItem name="name" required label="Nome" errors={validationErrors}>
          <Input />
        </FormItem>
        <FormItem
          name="email"
          required
          label="E-mail"
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem
          name="phone_number"
          label="Telefone"
          errors={validationErrors}
        >
          <Input />
        </FormItem>
        <FormItem label="Horas produtivas" errors={validationErrors}>
          <Form.Item name="monthly_productive_hours" noStyle>
            <InputNumber min={1} max={400} />
          </Form.Item>
          <span className="ant-form-text"> / mês</span>
        </FormItem>
        <FormItem name="role" required label="Função" errors={validationErrors}>
          <Select
            onChange={handleRoleChange}
            allowClear
            options={userRolesOptions}
          />
        </FormItem>
        <FormItem
          name="seniority"
          required
          label="Perfil"
          errors={validationErrors}
        >
          <Select allowClear>
            <Option value="JUNIOR">{t("seniorities.JUNIOR.label")}</Option>
            <Option value="FULL">{t("seniorities.FULL.label")}</Option>
            <Option value="SENIOR">{t("seniorities.SENIOR.label")}</Option>
          </Select>
        </FormItem>
        <FormItem
          name="hiring_model"
          required
          label="Contratação"
          errors={validationErrors}
        >
          <Select allowClear>
            <Option value="CLT">{t("hiring_models.CLT.label")}</Option>
            <Option value="CLT_TEMP">
              {t("hiring_models.CLT_TEMP.label")}
            </Option>
            <Option value="SHAREHOLDER">
              {t("hiring_models.SHAREHOLDER.label")}
            </Option>
            <Option value="CORPORATE">
              {t("hiring_models.CORPORATE.label")}
            </Option>
          </Select>
        </FormItem>
        <FormItem
          name="selected_skills"
          label="Skills"
          errors={validationErrors}
        >
          <CreatableSelect
            isMulti
            options={skillsOptions}
            isLoading={loadingSkills}
            placeholder=""
            formatCreateLabel={input => `Criar skill: ${input}`}
            onCreateOption={handleCreateSkill}
            styles={{
              control: base => ({
                ...base,
                borderRadius: 10,
                boxShadow: "none",
                border: "1px solid #d9d9d9",
                "&:hover": {
                  border: "1px solid orange",
                },
              }),
            }}
          />
        </FormItem>
        {!!selectedRole &&
          selectedRole !== UserRoles.Manager &&
          selectedRole !== UserRoles.Administrative && (
            <FormItem
              name="manager_id"
              required
              label="Gestor"
              errors={validationErrors}
            >
              <Select
                allowClear
                options={managerOptions}
                loading={loadingManagers}
              />
            </FormItem>
          )}
        <FormItem
          name="admission_date"
          label="Admissão"
          errors={validationErrors}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </FormItem>
        <FormItem
          name="dismissal_date"
          label="Demissão"
          errors={validationErrors}
        >
          <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
        </FormItem>
      </Form>
    </Modal>
  );
};

export default UserModal;
